import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { FormDialog } from '@ui-kit';
import { forbiddenWordSchema, type ForbiddenWordFormValues } from '../schema/terminologySchema';
import type { ForbiddenWord } from '../types/terminology';

const STATUS_OPTIONS = ['ACTIVE', 'DEPRECATED', 'PROPOSED'] as const;

interface ForbiddenWordFormDialogProps {
  open: boolean;
  editTarget: ForbiddenWord | null;
  isLoading: boolean;
  errorMessage?: string;
  onClose: () => void;
  onSubmit: (values: ForbiddenWordFormValues) => void;
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: 'var(--spacing-2) var(--spacing-3)',
  background: 'var(--color-surface-elevated)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-primary)',
  fontFamily: 'var(--font-family-sans)',
  outline: 'none',
  boxSizing: 'border-box',
};

const errorStyle: React.CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-danger)',
  marginTop: 'var(--spacing-1)',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 600,
  color: 'var(--color-text-primary)',
  marginBottom: 'var(--spacing-1)',
};

const fieldStyle: React.CSSProperties = {
  marginBottom: 'var(--spacing-3)',
};

export function ForbiddenWordFormDialog({
  open,
  editTarget,
  isLoading,
  errorMessage,
  onClose,
  onSubmit,
}: ForbiddenWordFormDialogProps) {
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ForbiddenWordFormValues>({
    resolver: zodResolver(forbiddenWordSchema),
    defaultValues: {
      bad: '',
      good: '',
      reason: '',
      source: 'MANUAL',
      status: 'ACTIVE',
    },
  });

  useEffect(() => {
    if (open) {
      if (editTarget) {
        reset({
          bad: editTarget.bad,
          good: editTarget.good,
          reason: editTarget.reason ?? '',
          source: editTarget.source ?? 'MANUAL',
          status: editTarget.status ?? 'ACTIVE',
        });
      } else {
        reset({
          bad: '',
          good: '',
          reason: '',
          source: 'MANUAL',
          status: 'ACTIVE',
        });
      }
    }
  }, [open, editTarget, reset]);

  const title = editTarget
    ? t('terminologyManagement.forbidden.form.title.edit', { id: editTarget.id })
    : t('terminologyManagement.forbidden.form.title.create');

  return (
    <FormDialog
      open={open}
      title={title}
      onClose={onClose}
      onCancel={onClose}
      onSubmit={handleSubmit(onSubmit)}
      submitLabel={isLoading ? t('terminologyManagement.form.button.loading') : t('terminologyManagement.form.button.submit')}
      cancelLabel={t('terminologyManagement.form.button.cancel')}
      submitTestId="forbidden-form-submit"
    >
      <div style={{ minWidth: 400, maxWidth: 520 }}>
        {/* Inline error */}
        {errorMessage && (
          <div
            role="alert"
            aria-live="assertive"
            style={{
              marginBottom: 'var(--spacing-3)',
              padding: 'var(--spacing-3)',
              background: 'var(--color-danger-light, #fef2f2)',
              border: '1px solid var(--color-danger)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-danger)',
            }}
          >
            {errorMessage}
          </div>
        )}

        {/* bad */}
        <div style={fieldStyle}>
          <label htmlFor="forbidden-bad" style={labelStyle}>
            {t('terminologyManagement.forbidden.form.field.bad')}{' '}
            <span aria-hidden="true" style={{ color: 'var(--color-danger)' }}>*</span>
          </label>
          <input
            id="forbidden-bad"
            type="text"
            autoComplete="off"
            aria-required="true"
            aria-invalid={!!errors.bad}
            {...register('bad')}
            style={inputStyle}
          />
          {errors.bad && (
            <p style={errorStyle} role="alert">
              {errors.bad.message}
            </p>
          )}
        </div>

        {/* good */}
        <div style={fieldStyle}>
          <label htmlFor="forbidden-good" style={labelStyle}>
            {t('terminologyManagement.forbidden.form.field.good')}{' '}
            <span aria-hidden="true" style={{ color: 'var(--color-danger)' }}>*</span>
          </label>
          <input
            id="forbidden-good"
            type="text"
            autoComplete="off"
            aria-required="true"
            aria-invalid={!!errors.good}
            {...register('good')}
            style={inputStyle}
          />
          {errors.good && (
            <p style={errorStyle} role="alert">
              {errors.good.message}
            </p>
          )}
        </div>

        {/* reason */}
        <div style={fieldStyle}>
          <label htmlFor="forbidden-reason" style={labelStyle}>
            {t('terminologyManagement.forbidden.form.field.reason')}
          </label>
          <textarea
            id="forbidden-reason"
            rows={2}
            {...register('reason')}
            style={{ ...inputStyle, resize: 'vertical' }}
          />
          {errors.reason && (
            <p style={errorStyle} role="alert">
              {errors.reason.message}
            </p>
          )}
        </div>

        {/* source + status */}
        <div style={{ display: 'flex', gap: 'var(--spacing-3)', marginBottom: 'var(--spacing-3)' }}>
          <div style={{ flex: 1 }}>
            <label htmlFor="forbidden-source" style={labelStyle}>
              {t('terminologyManagement.forbidden.form.field.source')}
            </label>
            <input
              id="forbidden-source"
              type="text"
              {...register('source')}
              style={inputStyle}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label htmlFor="forbidden-status" style={labelStyle}>
              {t('terminologyManagement.forbidden.form.field.status')}
            </label>
            <select
              id="forbidden-status"
              {...register('status')}
              style={{ ...inputStyle, cursor: 'pointer' }}
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {t(`terminologyManagement.status.${s}`)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </FormDialog>
  );
}
